import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { FiUploadCloud, FiImage, FiTrash2, FiExternalLink } from 'react-icons/fi';
import { FiFilePlus } from '@/components/react-icons/main';
import { get, del } from '@/lib/api';
import useFileUpload from '@/hooks/useFileUpload';
import Spinner from '@/components/Spinner';
import formatFileSize from './lib/formatFileSize.js';
import getFileIcon from './lib/getFileIcon.js';
import isImageMimeType from './lib/isImageMimeType.js';
import mergeFileMeta from './lib/mergeFileMeta.js';
import normalizeFileIds from './lib/normalizeFileIds.js';

const WizardFileUpload = ({ value, onChange, multiple = false, accept, maxSizeMb = 50, showPreview = true, disabled = false, mode = 'internal', shareToken = null, variant = 'default' }) => {
  const [fileMeta, setFileMeta] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [compactPopoverOpen, setCompactPopoverOpen] = useState(false);
  const fileInputRef = useRef(null);
  const fetchedIdsRef = useRef(new Set());
  const dragCounterRef = useRef(0);
  const isShareMode = mode === 'public';
  const isMissingShareToken = isShareMode && !shareToken;
  const isDisabled = disabled || isMissingShareToken;
  const isCompact = variant === 'compact';
  const { upload, isUploading, progress } = useFileUpload({ mode, shareToken });

  // Normalize value to always work with arrays internally
  const fileIds = normalizeFileIds(value);
  const fileIdsKey = fileIds.join(',');

  // Fetch file metadata for IDs we haven't fetched yet
  useEffect(() => {
    if (isMissingShareToken) {
      return;
    }

    const unfetchedIds = fileIds.filter((id) => !fetchedIdsRef.current.has(id) && !fileMeta[id]);

    if (unfetchedIds.length === 0) {
      return;
    }

    unfetchedIds.forEach((id) => fetchedIdsRef.current.add(id));

    const query = isShareMode ? { fileIds: unfetchedIds.join(','), token: shareToken } : { fileIds: unfetchedIds.join(',') };
    get('v1/files', {
      query,
      action: isShareMode ? 'GET_SHARED_FILE_DETAILS' : 'GET_FILE_DETAILS',
      callback: (data, err) => {
        if (err) {
          unfetchedIds.forEach((id) => fetchedIdsRef.current.delete(id));
          return;
        }

        const files = Array.isArray(data) ? data : data?.data;
        if (!Array.isArray(files) || files.length === 0) {
          unfetchedIds.forEach((id) => fetchedIdsRef.current.delete(id));
          return;
        }

        setFileMeta((prev) => mergeFileMeta({ prev, files }));
      }
    });
  }, [fileIdsKey, isMissingShareToken, isShareMode, shareToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiles = useCallback(
    async (selectedFiles) => {
      if (isDisabled || isUploading) {
        return;
      }

      const filesToUpload = Array.from(selectedFiles);
      const maxBytes = maxSizeMb * 1024 * 1024;

      // Client-side size validation
      const oversized = filesToUpload.find((f) => f.size > maxBytes);
      if (oversized) {
        return;
      }

      const result = await upload(filesToUpload);

      if (result?.success && result?.data) {
        // Cache the returned metadata
        setFileMeta((prev) => mergeFileMeta({ prev, files: result.data }));
        result.data.forEach((file) => {
          fetchedIdsRef.current.add(file.id);
        });

        const newIds = result.data.map((f) => f.id);

        if (multiple) {
          onChange([...fileIds, ...newIds]);
        } else {
          onChange(newIds[0] || null);
        }
      }
    },
    [isDisabled, isUploading, maxSizeMb, upload, multiple, fileIds, onChange]
  );

  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current += 1;
      if (!isDisabled && !isUploading) {
        setIsDragging(true);
      }
    },
    [isDisabled, isUploading]
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;

      if (isDisabled || isUploading) {
        return;
      }

      const droppedFiles = e.dataTransfer?.files;
      if (droppedFiles?.length > 0) {
        handleFiles(droppedFiles);
      }
    },
    [isDisabled, isUploading, handleFiles]
  );

  const handleBrowseClick = () => {
    if (isDisabled || isUploading) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles?.length > 0) {
      handleFiles(selectedFiles);
    }
    // Reset so selecting the same file triggers onChange again
    e.target.value = '';
  };

  const handleRemove = async (fileId) => {
    if (isMissingShareToken) {
      return;
    }

    setLoadingAction(`remove-${fileId}`);
    const query = isShareMode ? { token: shareToken } : undefined;
    await del(`v1/files/${fileId}`, { action: isShareMode ? 'DELETE_SHARED_FILE' : 'DELETE_FILE', query });
    setLoadingAction(null);

    setFileMeta((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
    fetchedIdsRef.current.delete(fileId);

    if (multiple) {
      onChange(fileIds.filter((id) => id !== fileId));
    } else {
      onChange(null);
    }
  };

  const handleView = async (fileId) => {
    if (isMissingShareToken) {
      return;
    }

    const popupWindow = window.open('about:blank', '_blank');
    if (popupWindow) {
      popupWindow.opener = null;
    }
    setLoadingAction(`view-${fileId}`);
    await get(`v1/files/${fileId}/signed-url`, {
      query: isShareMode ? { token: shareToken } : undefined,
      action: isShareMode ? 'GET_SHARED_FILE_SIGNED_URL' : 'GET_FILE_SIGNED_URL',
      callback: (data, err) => {
        if (err || !data?.url) {
          popupWindow?.close();
          return;
        }

        if (popupWindow) {
          popupWindow.location = data.url;
          popupWindow.focus();
          return;
        }

        window.open(data.url, '_blank');
      }
    });
    setLoadingAction(null);
  };

  // Compact variant: determine which circle to show for existing files
  const compactFirstFile = useMemo(() => {
    if (fileIds.length === 0) {
      return null;
    }
    const id = fileIds[0];
    const meta = fileMeta[id];
    return { id, meta, Icon: getFileIcon(meta?.mimeType) };
  }, [fileIds, fileMeta]);

  const compactExtraCount = fileIds.length > 1 ? fileIds.length - 1 : 0;

  // Compact variant render
  if (isCompact) {
    return (
      <div className="comp-wizard-file-upload compact">
        <input ref={fileInputRef} type="file" accept={accept} multiple={multiple} onChange={handleInputChange} style={{ display: 'none' }} />

        {fileIds.length > 0 && (
          <Popover.Root open={compactPopoverOpen} onOpenChange={setCompactPopoverOpen}>
            <Popover.Trigger asChild>
              <div className="compact-files-trigger">
                {compactFirstFile && (
                  <div className="compact-circle file-circle" style={{ zIndex: 1 }} data-title={compactFirstFile.meta?.name || 'File'}>
                    <compactFirstFile.Icon size={14} />
                  </div>
                )}
                {compactExtraCount > 0 && (
                  <div className="compact-circle extra-circle" style={{ zIndex: 2 }} data-title={`${compactExtraCount}+ more`}>
                    <span className="extra-count">{compactExtraCount}+</span>
                  </div>
                )}
              </div>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content className="comp-wizard-file-upload-compact-popover" align="start" side="bottom" sideOffset={6}>
                <div className="compact-popover-list">
                  {fileIds.map((fileId) => {
                    const meta = fileMeta[fileId];
                    const FileIcon = getFileIcon(meta?.mimeType);
                    const isRemoving = loadingAction === `remove-${fileId}`;
                    const isViewing = loadingAction === `view-${fileId}`;

                    return (
                      <div key={fileId} className="compact-popover-item">
                        <div className="compact-popover-icon">
                          <FileIcon size={14} />
                        </div>
                        <span className="compact-popover-name">{meta?.name || fileId}</span>
                        <div className="compact-popover-actions">
                          <button className="file-action-btn" onClick={() => handleView(fileId)} disabled={isViewing || isRemoving} data-title="View file">
                            {isViewing ? <Spinner size={12} /> : <FiExternalLink size={12} />}
                          </button>
                          {!isDisabled && (
                            <button className="file-action-btn danger" onClick={() => handleRemove(fileId)} disabled={isRemoving || isViewing} data-title="Remove file">
                              {isRemoving ? <Spinner size={12} /> : <FiTrash2 size={12} />}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}

        <div className={`compact-circle add-circle ${isDragging ? 'dragging' : ''} ${isDisabled || isUploading ? 'disabled' : ''}`} style={{ zIndex: 3 }} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop} onClick={handleBrowseClick} data-title="Add file">
          {isUploading ? <Spinner size={14} /> : <FiFilePlus size={14} />}
        </div>
      </div>
    );
  }

  // Default variant render
  const showDropZone = multiple || fileIds.length === 0;

  return (
    <div className="comp-wizard-file-upload">
      {showDropZone && (
        <div className={`drop-zone ${isDragging ? 'dragging' : ''} ${isDisabled || isUploading ? 'disabled' : ''}`} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop} onClick={handleBrowseClick}>
          <input ref={fileInputRef} type="file" accept={accept} multiple={multiple} onChange={handleInputChange} style={{ display: 'none' }} />

          {isUploading && (
            <div className="upload-progress">
              <Spinner size={16} />
              <span className="progress-text">Uploading... {progress}%</span>
              <div className="progress-bar-wrapper">
                <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {!isUploading && (
            <div className="drop-zone-content">
              <FiUploadCloud size={24} />
              <span className="drop-zone-label">Drag and drop files here, or click to browse</span>
              {maxSizeMb && <span className="drop-zone-hint">Max {maxSizeMb} MB per file</span>}
            </div>
          )}
        </div>
      )}

      {fileIds.length > 0 && (
        <div className="file-list">
          {fileIds.map((fileId) => {
            const meta = fileMeta[fileId];
            const FileIcon = getFileIcon(meta?.mimeType);
            const isImage = isImageMimeType(meta?.mimeType);
            const isRemoving = loadingAction === `remove-${fileId}`;
            const isViewing = loadingAction === `view-${fileId}`;

            return (
              <div key={fileId} className="file-item">
                <div className="file-icon">{showPreview && isImage && meta ? <FiImage size={18} /> : <FileIcon size={18} />}</div>
                <div className="file-info">
                  <span className="file-name">{meta?.name || fileId}</span>
                  {meta?.size && <span className="file-size">{formatFileSize(meta.size)}</span>}
                </div>
                <div className="file-actions">
                  <button className="file-action-btn" onClick={() => handleView(fileId)} disabled={isViewing || isRemoving} data-title="View file">
                    {isViewing ? <Spinner size={14} /> : <FiExternalLink size={14} />}
                  </button>
                  {!isDisabled && (
                    <button className="file-action-btn danger" onClick={() => handleRemove(fileId)} disabled={isRemoving || isViewing} data-title="Remove file">
                      {isRemoving ? <Spinner size={14} /> : <FiTrash2 size={14} />}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WizardFileUpload;
